

import React, { useEffect, useState } from 'react';
import { ShoppingBag, LayoutDashboard, ChevronRight, Zap, ArrowRight } from 'lucide-react';
import { useStore } from '../../context/StoreContext';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';

export const CustomerLayout: React.FC<{ children: React.ReactNode, showCart?: boolean }> = ({ children, showCart = true }) => {
  const { restaurant, business } = useStore();
  const { cartCount, cartTotal } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => window.scrollY > 50;
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!restaurant) return null;

  const isAdmin = user && business && business.id === restaurant.id;
  const theme = restaurant.styles.designTheme || 'neo-expression';

  // UI Adaptive Styles
  const headerStyles = {
      'neo-expression': scrolled ? 'bg-white border-b-[8px] border-black py-4' : 'bg-transparent py-8',
      'flat-pop': scrolled ? 'bg-brand-cyan border-b-4 border-black py-4' : 'bg-transparent py-6',
      'midnight-glitch': scrolled ? 'bg-black border-b border-brand-cyan/40 py-4' : 'bg-transparent py-8',
      'retro-vapor': scrolled ? 'glass py-4 m-4 rounded-[2rem]' : 'bg-transparent py-6'
  }[theme] || 'bg-white py-4';

  const cartButtonStyles = {
      'neo-expression': 'bg-brand-yellow border-[6px] border-black shadow-bold',
      'midnight-glitch': 'bg-black border border-brand-cyan text-brand-cyan shadow-neon-cyan',
      'retro-vapor': 'glass rounded-full text-brand-vaporpurple shadow-xl'
  }[theme] || 'bg-black text-white rounded-full';

  return (
    <div className={`min-h-screen font-sans text-black overflow-x-hidden selection:bg-brand-yellow`}>
      <header className={`fixed top-0 left-0 w-full z-50 px-6 lg:px-12 flex items-center justify-between transition-all duration-500 ${headerStyles}`}>
        <Link to={`/${restaurant.slug}`} className="flex items-center gap-4">
            <div className={`p-1 bg-black border-2 border-black overflow-hidden shadow-bold-sm ${theme === 'midnight-glitch' ? 'border-brand-cyan shadow-neon-cyan' : ''}`}>
                <img src={restaurant.logoUrl} className="w-12 h-12 object-cover" alt="Logo" />
            </div>
            <div className={`${scrolled ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'} transition-all hidden sm:block`}>
                {/* FIX: Property 'name' does not exist on type 'InstagramProfile'. Use 'username' instead. */}
                <h1 className={`font-black text-xl uppercase italic tracking-tighter ${theme === 'midnight-glitch' ? 'text-brand-cyan font-mono' : theme === 'retro-vapor' ? 'text-brand-vaporpurple' : 'text-black'}`}>{restaurant.username}</h1>
            </div>
        </Link>
        
        <div className="flex items-center gap-4">
            {isAdmin && <Link to="/admin/dashboard" className="hidden sm:flex bg-brand-green border-2 border-black px-4 py-2 font-black text-[9px] uppercase shadow-bold-sm">DASHBOARD</Link>}
            <button onClick={() => navigate(`/${restaurant.slug}/checkout`)} className={`relative w-14 h-14 lg:w-16 lg:h-16 flex items-center justify-center transition-all ${cartButtonStyles}`}>
                <ShoppingBag className="w-6 h-6 lg:w-8 lg:h-8" />
                {cartCount > 0 && <span className="absolute -top-2 -right-2 bg-brand-rose text-white text-[10px] font-black w-6 h-6 border-2 border-black flex items-center justify-center rotate-12">{cartCount}</span>}
            </button>
        </div>
      </header>

      <main className="min-h-screen">{children}</main>

      {showCart && cartCount > 0 && (
        <div className="fixed bottom-10 left-6 right-6 lg:left-auto lg:right-10 z-[100] lg:w-[400px]">
          <button onClick={() => navigate(`/${restaurant.slug}/checkout`)} className={`w-full flex items-center justify-between p-6 border-black transition-all shadow-bold-xl
                ${theme === 'neo-expression' ? 'bg-black text-white border-[6px]' : ''}
                ${theme === 'midnight-glitch' ? 'bg-black text-brand-cyan border border-brand-cyan shadow-neon-cyan font-mono' : ''}
                ${theme === 'retro-vapor' ? 'glass text-brand-vaporpurple border-0 rounded-full' : ''}
                ${!theme.includes('midnight') && !theme.includes('vapor') ? 'bg-brand-yellow text-black border-4' : ''}
            `}>
            <div className="flex items-center gap-4">
              <div className="bg-white text-black w-10 h-10 border-2 border-black flex items-center justify-center font-black italic shadow-bold-sm rotate-6">{cartCount}</div>
              <p className="text-2xl font-black italic tracking-tighter uppercase">R$ {cartTotal.toFixed(2)}</p>
            </div>
            <ArrowRight className="w-8 h-8" />
          </button>
        </div>
      )}
    </div>
  );
};